import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { SectionLabel, Headline, Field, Stamp } from "@/components/Doc";

const interests = [
  ["scoping-call", "A scoping call"],
  ["memorandum", "The engagement memorandum"],
  ["dealer-memorandum", "The dealer memorandum"],
  ["findings-review", "The Findings Review"],
  ["watch-standard", "WATCH — Standard"],
  ["watch-tradecraft", "WATCH — Tradecraft"],
  ["watch-full", "WATCH — Full"],
  ["module-tradecraft", "Module I — Tradecraft"],
  ["module-analog-office", "Module II — The Analog Office"],
  ["module-operators", "Module III — The Operators"],
  ["module-command", "Module IV — Command"],
];

export default function Contact() {
  const [params] = useSearchParams();
  const initial = interests.some(([k]) => k === params.get("interest")) ? params.get("interest") : "scoping-call";
  const [f, setF] = useState({ name: "", email: "", company: "", role: "", phone: "", locationCount: "", interest: initial, message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, locationCount: f.locationCount === "" ? null : Number(f.locationCount) });
      setDone(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="contact-page">
      <SectionLabel right="Form LH-12">Doc. 08 — Enquiry</SectionLabel>
      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-5">
          <Headline as="h1" className="text-4xl sm:text-5xl lg:text-6xl">Book a scoping call.</Headline>
          <p className="t-body text-lg sm:text-xl mt-6 leading-[1.65] max-w-[45ch]">Forty minutes with the sponsor. We'll ask what one credential reaches, who can move money, and what has already gone wrong. We'll tell you whether a single module or the full install is the right shape, or whether you don't need us.</p>
          <p className="t-body text-base mt-6 text-olive italic">We reply to every enquiry by hand, usually within two working days.</p>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <div className="settle bg-paper border border-carbon p-10 relative shadow-[6px_6px_0_0_#D9C9A8]" data-testid="contact-success">
              <div className="absolute -top-5 right-8"><Stamp color="red" rotate={-6} size="lg">Received</Stamp></div>
              <span className="t-label text-olive">Form LH-12 — Filed</span>
              <h2 className="t-head text-3xl mt-3">Enquiry received.</h2>
              <p className="t-body text-lg mt-4 leading-[1.65]">Thank you, {f.name}. Your enquiry has been filed. Someone from the firm will write to {f.email} within two working days.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-paper border border-carbon p-7 sm:p-10 shadow-[6px_6px_0_0_#D9C9A8] flex flex-col gap-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Name" name="name" value={f.name} onChange={set} required testId="contact-name" />
                <Field label="Work email" name="email" type="email" value={f.email} onChange={set} required testId="contact-email" />
                <Field label="Company" name="company" value={f.company} onChange={set} testId="contact-company" />
                <Field label="Role" name="role" value={f.role} onChange={set} testId="contact-role" />
                <Field label="Phone" name="phone" value={f.phone} onChange={set} testId="contact-phone" />
                <Field label="Location count" name="locationCount" type="number" value={f.locationCount} onChange={set} testId="contact-location-count" />
              </div>
              <Field label="Regarding" name="interest" as="select" value={f.interest} onChange={set} testId="contact-interest">
                {interests.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Field>
              <Field label="Notes" name="message" as="textarea" value={f.message} onChange={set} placeholder="What one credential reaches, who can move money, what has already gone wrong." testId="contact-message" />
              <button type="submit" className="btn btn-stamp self-start" disabled={busy} data-testid="contact-submit-btn">{busy ? "Filing…" : "Submit Enquiry"}</button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
